import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { PageLoader } from "@/components/common/loader";
import { AppLayout } from "@/layouts/app-layout";
import { AuthLayout } from "@/layouts/auth-layout";
import { ClientLayout } from "@/layouts/client-layout";
import { RootLayout } from "@/layouts/root-layout";
import { SettingsLayout } from "@/layouts/settings-layout";
import { PortalLayout } from "@/layouts/portal-layout";
import { AdminLayout } from "@/layouts/admin-layout";
import { OpsLayout } from "@/layouts/ops-layout";
import { KbLayout } from "@/layouts/kb-layout";
import { OnboardingGate } from "@/routes/onboarding-gate";
import { PortalRoute } from "@/routes/portal-route";
import { isPortalHost, portalHref } from "@/features/portal/portal-base";
import { PrivateRoute } from "@/routes/private-route";
import { PlatformAdminRoute } from "@/routes/platform-admin-route";
import { BpDirectoryRoute } from "@/routes/bp-directory-route";
import { TeamRoute } from "@/routes/team-route";
import { PublicRoute } from "@/routes/public-route";
import { useAuthStore } from "@/features/auth/store/auth-store";

// Route-level code splitting: each page ships in its own chunk and is only
// fetched when the user navigates to it.
const LoginPage = lazy(() => import("@/pages/login-page"));
const OtpVerificationPage = lazy(() => import("@/pages/otp-verification-page"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password-page"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password-page"));
const DashboardPage = lazy(() => import("@/pages/dashboard-page"));
const SearchPage = lazy(() => import("@/pages/search-page"));
// First-login firm-owner "Getting started" hub — rendered inside the AppLayout
// shell (see OnboardingGate), reached from the sidebar's "Getting started" pill.
const OnboardingPage = lazy(() => import("@/pages/onboarding-page"));
const ClientsPage = lazy(() => import("@/pages/clients-page"));
const NewArrivalsPage = lazy(() => import("@/pages/new-arrivals-page"));
const ChatPage = lazy(() => import("@/pages/chat-page"));
const InvoicesPage = lazy(() => import("@/pages/invoices-page"));
// Personal account pages — reached from the header account menu, outside the
// settings tab strip, each with the standard title/subtitle page layout.
const ProfilePage = lazy(() => import("@/pages/profile-page"));
const TeamPage = lazy(() => import("@/pages/team-page"));
const PractitionersPage = lazy(() => import("@/pages/practitioners-page"));
// Business-Practitioner directory. Gated by BpDirectoryRoute (GA/SA or a house
// Team-Leader+); the list is scoped to the caller's assigned BPs server-side.
const BusinessPractitionersPage = lazy(() => import("@/pages/business-practitioners-page"));
// Settings sub-pages — one lazy chunk per tab route (named exports).
const SettingsCompanyTab = lazy(() =>
  import("@/features/settings/components/tabs/company-tab").then((m) => ({
    default: m.CompanyTab,
  })),
);
const SettingsPractitionerAllocation = lazy(() =>
  import("@/features/practitioners/components/allocation-page").then((m) => ({
    default: m.PractitionerAllocationPage,
  })),
);
const SettingsLettersTab = lazy(() =>
  import("@/features/settings/components/tabs/letters-tab").then((m) => ({
    default: m.LettersTab,
  })),
);
const SettingsInvoicesTab = lazy(() =>
  import("@/features/settings/components/tabs/invoices-tab").then((m) => ({
    default: m.InvoicesTab,
  })),
);
const SettingsInvoiceTemplatesTab = lazy(() =>
  import("@/features/settings/components/tabs/invoice-templates-tab").then((m) => ({
    default: m.InvoiceTemplatesTab,
  })),
);
const SettingsPortalTab = lazy(() =>
  import("@/features/settings/components/tabs/portal-tab").then((m) => ({ default: m.PortalTab })),
);
const SettingsApiTab = lazy(() =>
  import("@/features/settings/components/tabs/api-tab").then((m) => ({ default: m.ApiTab })),
);
const SettingsDefaultsTab = lazy(() =>
  import("@/features/settings/components/tabs/defaults-tab").then((m) => ({
    default: m.DefaultsTab,
  })),
);
const SettingsFoldersTab = lazy(() =>
  import("@/features/settings/components/tabs/folders-tab").then((m) => ({
    default: m.FoldersTab,
  })),
);
const SettingsNoticeTemplatesTab = lazy(() =>
  import("@/features/settings/components/tabs/notice-templates-tab").then((m) => ({
    default: m.NoticeTemplatesTab,
  })),
);
const SettingsESignatureTab = lazy(() =>
  import("@/features/settings/components/tabs/esignature-tab").then((m) => ({
    default: m.ESignatureTab,
  })),
);
// Reference Data (ops-only) — its own staff-gated layout + sub-nav.
const ReferenceLayout = lazy(() =>
  import("@/features/reference/components/reference-layout").then((m) => ({
    default: m.ReferenceLayout,
  })),
);
const ReferenceHome = lazy(() =>
  import("@/features/reference/components/reference-home").then((m) => ({
    default: m.ReferenceHome,
  })),
);
const ReferenceNotices = lazy(() =>
  import("@/features/reference/components/notices-page").then((m) => ({ default: m.NoticesPage })),
);
const ReferenceTaxForms = lazy(() =>
  import("@/features/reference/components/tax-forms-page").then((m) => ({
    default: m.TaxFormsPage,
  })),
);
const ReferenceStandards = lazy(() =>
  import("@/features/reference/components/standards-page").then((m) => ({
    default: m.StandardsPage,
  })),
);
const ReferenceTransactionCodes = lazy(() =>
  import("@/features/reference/components/transaction-codes-page").then((m) => ({
    default: m.TransactionCodesPage,
  })),
);
const ReferenceTranscriptTypes = lazy(() =>
  import("@/features/reference/components/transcript-types-page").then((m) => ({
    default: m.TranscriptTypesPage,
  })),
);
const AcceptInvitePage = lazy(() => import("@/pages/accept-invite-page"));
const NewClientPage = lazy(() => import("@/pages/new-client-page"));
const ClientOverviewPage = lazy(() => import("@/pages/client/overview-page"));
const ClientProfilePage = lazy(() => import("@/pages/client/profile-page"));
const ClientComparisonPage = lazy(() => import("@/pages/client/comparison-page"));
const ClientTasksPage = lazy(() => import("@/pages/client/tasks-page"));
const ClientCasesPage = lazy(() => import("@/pages/client/cases-page"));
const ClientCaseDetailPage = lazy(() => import("@/pages/client/cases/case-detail-page"));
const ClientWorksheetsPage = lazy(() => import("@/pages/client/cases/worksheets/worksheets-page"));
const ClientIAWorkflowPage = lazy(() => import("@/pages/client/cases/workflow/ia-workflow-page"));
const ClientOICWorkflowPage = lazy(() => import("@/pages/client/cases/workflow/oic-workflow-page"));
const ClientInnocentSpouseWorkflowPage = lazy(
  () => import("@/pages/client/workflows/innocent-spouse-page"),
);
const ClientSpouseReliefWorkflowPage = lazy(
  () => import("@/pages/client/workflows/spouse-relief-page"),
);
const ClientCaseSpouseReliefWorkflowPage = lazy(
  () => import("@/pages/client/cases/workflow/spouse-relief-page"),
);
const ClientExciseWorkflowPage = lazy(
  () => import("@/pages/client/cases/workflow/excise-workflow-page"),
);
const ClientIrsFormsPage = lazy(() => import("@/pages/client/irs-forms-page"));
const ClientFormFillPage = lazy(() => import("@/pages/client/irs-forms/form-fill-page"));
const ClientDocumentsPage = lazy(() => import("@/pages/client/documents-page"));
const ClientRequiredDocsPage = lazy(() => import("@/pages/client/required-docs-page"));
const ClientLastYearDocsPage = lazy(() => import("@/pages/client/last-year-docs-page"));
const ClientBankingPage = lazy(() => import("@/pages/client/banking-page"));
const ClientFinancialsPage = lazy(() => import("@/pages/client/financials-page"));
const ClientIrsNoticesPage = lazy(() => import("@/pages/client/irs-notices-page"));
const ClientNoticeDetailPage = lazy(() => import("@/pages/client/notices/notice-detail-page"));
const ClientLettersPage = lazy(() => import("@/pages/client/letters-page"));
const ClientESignaturesPage = lazy(() => import("@/pages/client/esignatures/esignatures-page"));
const ClientESignatureDetailPage = lazy(
  () => import("@/pages/client/esignatures/request-detail-page"),
);
const ClientEFilingPage = lazy(() => import("@/pages/client/efiling/submission-center-page"));
const ClientSubmissionDetailPage = lazy(
  () => import("@/pages/client/efiling/submission-detail-page"),
);
const ESignaturesReportPage = lazy(() => import("@/pages/reports/esignatures-report-page"));
const ReportsHubPage = lazy(() => import("@/pages/reports/reports-hub-page"));
const ReportPage = lazy(() => import("@/pages/reports/report-page"));
const TaxReliefReportPage = lazy(() => import("@/pages/client/tax-relief-report-page"));
const ClientInvoicesListPage = lazy(() => import("@/pages/client/invoices/invoices-list-page"));
const ClientInvoiceEditPage = lazy(() => import("@/pages/client/invoices/invoice-edit-page"));
const ClientTranscriptsLayout = lazy(() =>
  import("@/pages/client/transcripts/transcripts-layout").then((m) => ({
    default: m.ClientTranscriptsLayout,
  })),
);
const ClientTaxOwedPage = lazy(() => import("@/pages/client/transcripts/tax-owed-page"));
const ClientTranscriptDetailPage = lazy(
  () => import("@/pages/client/transcripts/transcript-detail-page"),
);
const ClientPaymentsPage = lazy(() => import("@/pages/client/transcripts/payments-page"));
const ClientIncomePage = lazy(() => import("@/pages/client/transcripts/income-page"));
const ClientFtaPage = lazy(() => import("@/pages/client/transcripts/fta-page"));
const ClientBankruptcyPage = lazy(() => import("@/pages/client/transcripts/bankruptcy-page"));
const TranscriptRequestsPage = lazy(() =>
  import("@/pages/client/transcripts/transcript-requests-page").then((m) => ({
    default: m.TranscriptRequestsPage,
  })),
);
const TranscriptMonitoringPage = lazy(() =>
  import("@/pages/client/transcripts/transcript-monitoring-page").then((m) => ({
    default: m.TranscriptMonitoringPage,
  })),
);
// e-Services (05-2)
const EServicesHubPage = lazy(() =>
  import("@/pages/eservices/eservices-hub-page").then((m) => ({ default: m.EServicesHubPage })),
);
const PoaSubmissionPage = lazy(() =>
  import("@/pages/eservices/poa-submission-page").then((m) => ({ default: m.PoaSubmissionPage })),
);
const CafMonitorPage = lazy(() =>
  import("@/pages/eservices/caf-monitor-page").then((m) => ({ default: m.CafMonitorPage })),
);
const SecureMailboxPage = lazy(() =>
  import("@/pages/eservices/secure-mailbox-page").then((m) => ({ default: m.SecureMailboxPage })),
);
const SettingsIntegrationsTab = lazy(() =>
  import("@/features/settings/components/tabs/integrations-tab").then((m) => ({
    default: m.IntegrationsTab,
  })),
);
const SettingsSignaturesTab = lazy(() =>
  import("@/features/settings/components/tabs/signatures-tab").then((m) => ({
    default: m.SignaturesTab,
  })),
);
const SettingsTagsTab = lazy(() =>
  import("@/features/settings/components/tabs/tags-tab").then((m) => ({ default: m.TagsTab })),
);
const SettingsNotificationsTab = lazy(() =>
  import("@/features/settings/components/tabs/notifications-tab").then((m) => ({
    default: m.NotificationsTab,
  })),
);
const SettingsEmailsTab = lazy(() =>
  import("@/features/settings/components/tabs/emails-tab").then((m) => ({ default: m.EmailsTab })),
);
const SettingsEmailTemplateEdit = lazy(() =>
  import("@/features/settings/components/tabs/email-template-edit").then((m) => ({
    default: m.EmailTemplateEditPage,
  })),
);
// Client portal (05-4) — taxpayer-facing, own layout + portal-session guard.
const PortalLandingPage = lazy(() => import("@/pages/portal/portal-landing-page"));
const PortalEntryPage = lazy(() => import("@/pages/portal/portal-entry-page"));
const OnboardingEntryPage = lazy(() => import("@/pages/portal/onboarding-entry-page"));
const OnboardingWizardPage = lazy(() => import("@/pages/portal/onboarding-wizard-page"));
const OnboardingDonePage = lazy(() => import("@/pages/portal/onboarding-done-page"));
const PortalHomePage = lazy(() => import("@/pages/portal/portal-home-page"));
const PortalFinancialsPage = lazy(() => import("@/pages/portal/financials-page"));
const PortalBusinessDetailPage = lazy(() => import("@/pages/portal/business-detail-page"));
const PortalDocumentsPage = lazy(() => import("@/pages/portal/documents-page"));
const PortalUploadPage = lazy(() => import("@/pages/portal/upload-page"));
const PortalRequestedDocsPage = lazy(() => import("@/pages/portal/requested-docs-page"));
const PortalClientProfilePage = lazy(() => import("@/pages/portal/profile-page"));
const PortalNoticesPage = lazy(() => import("@/pages/portal/notices-page"));
const PortalProposalsPage = lazy(() => import("@/pages/portal/proposals-page"));
const PortalChangePasswordPage = lazy(() => import("@/pages/portal/change-password-page"));
const PortalSwitchClientPage = lazy(() => import("@/pages/portal/switch-client-page"));
const PortalNeedCookiesPage = lazy(() => import("@/pages/portal/need-cookies-page"));

// Platform Admin / Control Center (05-6) — super-admin-only, own guard + shell.
const AdminHomePage = lazy(() => import("@/pages/admin/admin-home-page"));
const AdminWhatsNewPage = lazy(() => import("@/pages/admin/whatsnew-page"));
const AdminBroadcastPage = lazy(() => import("@/pages/admin/broadcast-page"));
const AdminEventsPage = lazy(() => import("@/pages/admin/events-page"));
const AdminResourcesPage = lazy(() => import("@/pages/admin/resources-page"));
const AdminToolboxPage = lazy(() => import("@/pages/admin/toolbox-page"));
const AdminVideosPage = lazy(() => import("@/pages/admin/videos-page"));
const AdminMediaPage = lazy(() => import("@/pages/admin/media-page"));
const AdminFilesPage = lazy(() => import("@/pages/admin/files-page"));
const AdminTranscheckPage = lazy(() => import("@/pages/admin/transcheck-page"));
const AdminTranscheckRulesPage = lazy(() => import("@/pages/admin/transcheck-rules-page"));
const AdminWaidsMapPage = lazy(() => import("@/pages/admin/waidsmap-page"));
const AdminSystemSettingsPage = lazy(() => import("@/pages/admin/system-settings-page"));

// Ops tooling (05-7) — staff-only, under the same platform-admin guard.
const OpsHomePage = lazy(() => import("@/pages/admin/ops/ops-home-page"));
const OpsUserLogPage = lazy(() => import("@/pages/admin/ops/user-log-page"));
const OpsCodesPage = lazy(() => import("@/pages/admin/ops/codes-page"));
const OpsTranscriptDetailPage = lazy(() => import("@/pages/admin/ops/transcript-detail-page"));
const OpsCsedAuditPage = lazy(() => import("@/pages/admin/ops/csed-audit-page"));
const OpsTdsHealthPage = lazy(() => import("@/pages/admin/ops/tds-health-page"));
const OpsErrorsPage = lazy(() => import("@/pages/admin/ops/errors-page"));
const OpsBadA2APage = lazy(() => import("@/pages/admin/ops/bad-a2a-page"));
const OpsReportsPage = lazy(() => import("@/pages/admin/ops/reports-page"));
const OpsTestUsersPage = lazy(() => import("@/pages/admin/ops/test-users-page"));
const OpsJobsPage = lazy(() => import("@/pages/admin/ops/jobs-page"));

// Knowledge Base (05-8) — firm-facing browsing + platform-admin authoring.
const KbHomePage = lazy(() => import("@/pages/kb/kb-home-page"));
const FaqBrowsePage = lazy(() => import("@/pages/kb/faq-browse-page"));
const FaqTranscriptsPage = lazy(() => import("@/pages/kb/faq-transcripts-page"));
const FaqManagePage = lazy(() => import("@/pages/kb/faq-manage-page"));
const WikiViewPage = lazy(() => import("@/pages/kb/wiki-view-page"));
const WikiManagePage = lazy(() => import("@/pages/kb/wiki-manage-page"));
const KbWhatsNewPage = lazy(() => import("@/pages/kb/whats-new-page"));
const LibraryHomePage = lazy(() => import("@/pages/kb/library-home-page"));
const LibraryCategoryPage = lazy(() => import("@/pages/kb/library-category-page"));
const LibraryVideosPage = lazy(() => import("@/pages/kb/library-videos-page"));
const KbToolboxPage = lazy(() => import("@/pages/kb/toolbox-page"));
const KbVideosPage = lazy(() => import("@/pages/kb/videos-page"));

const NotFoundPage = lazy(() => import("@/pages/not-found-page"));

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

/** The app has no marketing home page — send `/` to the dashboard (signed in)
 * or the login screen (signed out). */
function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

// Portal routes serve two ways from one bundle: at the root of the dedicated
// portal host (`portal.<host>/<handle>`), or under `/portal/...` on the staff
// host + single-host local dev. `portalPath` emits the right route path for the
// active mode (see features/portal/portal-base.ts).
const portalPath = (sub: string) =>
  isPortalHost ? sub : sub ? `portal/${sub}` : "portal";

// Public, firm-branded client portal (05-4) — its own layout + session guard,
// entirely outside the staff PublicRoute/PrivateRoute shells.
const portalPublicGroup = {
  element: <PortalLayout />,
  children: [
    isPortalHost
      ? { index: true, element: withSuspense(<PortalLandingPage />) }
      : { path: "portal", element: withSuspense(<PortalLandingPage />) },
    { path: portalPath("onboarding/done"), element: withSuspense(<OnboardingDonePage />) },
    { path: portalPath("onboarding/:token"), element: withSuspense(<OnboardingEntryPage />) },
    { path: portalPath("onboarding"), element: withSuspense(<OnboardingWizardPage />) },
    { path: portalPath("cookies"), element: withSuspense(<PortalNeedCookiesPage />) },
    // Dynamic handle routes last so the static portal paths above win (React
    // Router ranks static > dynamic); reserved-handle validation also blocks a
    // firm from claiming one of those words.
    { path: portalPath(":portalName"), element: withSuspense(<PortalEntryPage />) },
    { path: portalPath(":portalName/auth"), element: withSuspense(<PortalEntryPage />) },
  ],
};

// Authenticated portal surfaces — gated by the portal session guard.
const portalAuthedGroup = {
  element: <PortalRoute />,
  children: [
    {
      // `authed` turns on the section nav + account menu. The public group uses
      // the same layout WITHOUT this flag, so a persisted session can't leak the
      // nav onto the sign-in / onboarding screens.
      element: <PortalLayout authed />,
      children: [
        { path: portalPath("home"), element: withSuspense(<PortalHomePage />) },
        { path: portalPath("profile"), element: withSuspense(<PortalClientProfilePage />) },
        { path: portalPath("notices"), element: withSuspense(<PortalNoticesPage />) },
        { path: portalPath("proposals"), element: withSuspense(<PortalProposalsPage />) },
        {
          path: portalPath("financials"),
          element: <Navigate to={portalHref("/financials/personal")} replace />,
        },
        {
          path: portalPath("financials/business/:businessId"),
          element: withSuspense(<PortalBusinessDetailPage />),
        },
        { path: portalPath("financials/:tab"), element: withSuspense(<PortalFinancialsPage />) },
        { path: portalPath("documents"), element: withSuspense(<PortalDocumentsPage />) },
        { path: portalPath("documents/upload"), element: withSuspense(<PortalUploadPage />) },
        {
          path: portalPath("requested-documents"),
          element: withSuspense(<PortalRequestedDocsPage />),
        },
        { path: portalPath("password"), element: withSuspense(<PortalChangePasswordPage />) },
        { path: portalPath("switch-client"), element: withSuspense(<PortalSwitchClientPage />) },
      ],
    },
  ],
};

const staffRouteTree = [
  {
    // Auth routes render full-bleed (no app header/max-width shell) so
    // AuthLayout's split-screen design isn't squeezed by RootLayout's <main>.
    element: <PublicRoute />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: "login", element: withSuspense(<LoginPage />) },
          { path: "verify-email", element: withSuspense(<OtpVerificationPage />) },
          { path: "forgot-password", element: withSuspense(<ForgotPasswordPage />) },
          { path: "reset-password", element: withSuspense(<ResetPasswordPage />) },
        ],
      },
    ],
  },
  {
    // Accept-invitation is token-driven, not an "auth-only" page — it must stay
    // reachable regardless of session state (unlike login, it isn't wrapped in
    // PublicRoute, which would bounce an already-signed-in visitor).
    element: <AuthLayout />,
    children: [{ path: "accept-invite", element: withSuspense(<AcceptInvitePage />) }],
  },
  {
    // Plain shell for unauthenticated/marketing pages — the app's sidebar +
    // header chrome (AppLayout) doesn't make sense before a user is signed in.
    element: <RootLayout />,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: "*", element: withSuspense(<NotFoundPage />) },
    ],
  },
  portalPublicGroup,
  portalAuthedGroup,
  {
    element: <PrivateRoute />,
    children: [
      {
        // OnboardingGate lands first-login firm admins on /onboarding once, then
        // lets them roam the shell; everyone else falls straight through.
        element: <OnboardingGate />,
        children: [
          {
            // Authenticated app shell: collapsible sidebar + top header (Stitch
            // "Control Center" design), shared by every private page.
            element: <AppLayout />,
            children: [
              {
                // First-login "Getting started" hub — now rendered INSIDE the
                // shell (sidebar + header visible), reached from the sidebar's
                // "Getting started" pill. The gate never redirects while on this
                // path, so there's no loop.
                path: "onboarding",
                element: withSuspense(<OnboardingPage />),
              },
              { path: "dashboard", element: withSuspense(<DashboardPage />) },
              { path: "search", element: withSuspense(<SearchPage />) },
              // Personal account pages (header account menu) — standalone, outside
              // the settings tab strip.
              { path: "profile", element: withSuspense(<ProfilePage />) },
              {
                // Team roster: house org → GA/SA only; a BP firm's own CD+ keeps
                // it for their own team (TeamRoute). Direct-URL access is bounced.
                path: "team",
                element: <TeamRoute />,
                children: [{ index: true, element: withSuspense(<TeamPage />) }],
              },
              {
                // Settings: a sub-nav layout with one route per tab so each is
                // independently linkable (/settings/profile, /settings/company, …).
                path: "settings",
                element: <SettingsLayout />,
                children: [
                  { index: true, element: <Navigate to="/settings/company" replace /> },
                  // Profile + Team moved to their own top-level pages (header account
                  // menu); keep the old settings paths as redirects for saved links.
                  { path: "profile", element: <Navigate to="/profile" replace /> },
                  { path: "team", element: <Navigate to="/team" replace /> },
                  { path: "company", element: withSuspense(<SettingsCompanyTab />) },
                  // Practitioners moved to its own top-level sidebar page; keep the
                  // old settings paths as redirects for saved links.
                  { path: "practitioners", element: <Navigate to="/practitioners" replace /> },
                  {
                    path: "practitioners/allocation",
                    element: <Navigate to="/practitioners/allocation" replace />,
                  },
                  { path: "letters", element: withSuspense(<SettingsLettersTab />) },
                  { path: "signatures", element: withSuspense(<SettingsSignaturesTab />) },
                  { path: "invoices", element: withSuspense(<SettingsInvoicesTab />) },
                  {
                    path: "invoices/templates",
                    element: withSuspense(<SettingsInvoiceTemplatesTab />),
                  },
                  { path: "portal", element: withSuspense(<SettingsPortalTab />) },
                  { path: "api", element: withSuspense(<SettingsApiTab />) },
                  { path: "defaults", element: withSuspense(<SettingsDefaultsTab />) },
                  { path: "tags", element: withSuspense(<SettingsTagsTab />) },
                  { path: "notifications", element: withSuspense(<SettingsNotificationsTab />) },
                  { path: "emails", element: withSuspense(<SettingsEmailsTab />) },
                  { path: "emails/:code", element: withSuspense(<SettingsEmailTemplateEdit />) },
                  { path: "folders", element: withSuspense(<SettingsFoldersTab />) },
                  {
                    path: "notice-templates",
                    element: withSuspense(<SettingsNoticeTemplatesTab />),
                  },
                  { path: "esignature", element: withSuspense(<SettingsESignatureTab />) },
                  { path: "integrations", element: withSuspense(<SettingsIntegrationsTab />) },
                ],
              },
              {
                // Reference Data: a separate ops-only area (own layout + staff
                // guard), not one of the personal/firm settings tabs above.
                path: "settings/reference",
                element: withSuspense(<ReferenceLayout />),
                children: [
                  { index: true, element: withSuspense(<ReferenceHome />) },
                  { path: "notices", element: withSuspense(<ReferenceNotices />) },
                  { path: "tax-forms", element: withSuspense(<ReferenceTaxForms />) },
                  { path: "standards", element: withSuspense(<ReferenceStandards />) },
                  {
                    path: "transaction-codes",
                    element: withSuspense(<ReferenceTransactionCodes />),
                  },
                  {
                    path: "transcript-types",
                    element: withSuspense(<ReferenceTranscriptTypes />),
                  },
                ],
              },
              {
                // Platform Admin / Control Center (05-6): vendor-owned global
                // content behind the super-admin guard, with its own sub-nav shell.
                path: "admin",
                element: <PlatformAdminRoute />,
                children: [
                  {
                    element: <AdminLayout />,
                    children: [
                      { index: true, element: withSuspense(<AdminHomePage />) },
                      { path: "whatsnew", element: withSuspense(<AdminWhatsNewPage />) },
                      { path: "broadcast", element: withSuspense(<AdminBroadcastPage />) },
                      { path: "events", element: withSuspense(<AdminEventsPage />) },
                      { path: "resources", element: withSuspense(<AdminResourcesPage />) },
                      { path: "toolbox", element: withSuspense(<AdminToolboxPage />) },
                      { path: "videos", element: withSuspense(<AdminVideosPage />) },
                      { path: "media", element: withSuspense(<AdminMediaPage />) },
                      { path: "files", element: withSuspense(<AdminFilesPage />) },
                      { path: "transcheck", element: withSuspense(<AdminTranscheckPage />) },
                      {
                        path: "transcheck/rules",
                        element: withSuspense(<AdminTranscheckRulesPage />),
                      },
                      { path: "waidsmap", element: withSuspense(<AdminWaidsMapPage />) },
                      { path: "settings", element: withSuspense(<AdminSystemSettingsPage />) },
                    ],
                  },
                ],
              },
              {
                // Ops Tooling (05-7): staff-only internal ops + background-jobs
                // console. Same platform-admin gate as the Control Center, its own
                // sub-nav shell. The one area that reads across every firm.
                path: "admin/ops",
                element: <PlatformAdminRoute />,
                children: [
                  {
                    element: <OpsLayout />,
                    children: [
                      { index: true, element: withSuspense(<OpsHomePage />) },
                      { path: "users/:userId/log", element: withSuspense(<OpsUserLogPage />) },
                      { path: "codes", element: withSuspense(<OpsCodesPage />) },
                      {
                        path: "transcripts/:id",
                        element: withSuspense(<OpsTranscriptDetailPage />),
                      },
                      { path: "csed", element: withSuspense(<OpsCsedAuditPage />) },
                      { path: "tds", element: withSuspense(<OpsTdsHealthPage />) },
                      { path: "errors", element: withSuspense(<OpsErrorsPage />) },
                      { path: "bad-a2a", element: withSuspense(<OpsBadA2APage />) },
                      { path: "reports", element: withSuspense(<OpsReportsPage />) },
                      { path: "test-users", element: withSuspense(<OpsTestUsersPage />) },
                      { path: "jobs", element: withSuspense(<OpsJobsPage />) },
                    ],
                  },
                ],
              },
              {
                // Business-Practitioner directory: GA/SA provision + manage every
                // BP; a house Team-Leader+ manages the BPs assigned to them
                // (scoped server-side). Provisioning stays GA/SA-only in the UI.
                path: "business-practitioners",
                element: <BpDirectoryRoute />,
                children: [{ index: true, element: withSuspense(<BusinessPractitionersPage />) }],
              },
              { path: "new-arrivals", element: withSuspense(<NewArrivalsPage />) },
              // /clients serves both the in-house (CAP) book and any Business
              // Practitioner the user has switched into — the active firm lives
              // in the firm-context store, not the URL.
              { path: "clients", element: withSuspense(<ClientsPage />) },
              { path: "clients/new", element: withSuspense(<NewClientPage />) },
              { path: "chat", element: withSuspense(<ChatPage />) },
              // Practitioners — standalone sidebar page (moved out of Settings).
              { path: "practitioners", element: withSuspense(<PractitionersPage />) },
              {
                path: "practitioners/allocation",
                element: withSuspense(<SettingsPractitionerAllocation />),
              },
              { path: "invoices", element: withSuspense(<InvoicesPage />) },
              { path: "reports", element: withSuspense(<ReportsHubPage />) },
              // Static route resolves ahead of the dynamic slug below.
              { path: "reports/esignatures", element: withSuspense(<ESignaturesReportPage />) },
              { path: "reports/:reportKey", element: withSuspense(<ReportPage />) },
              // e-Services (05-2)
              { path: "eservices", element: withSuspense(<EServicesHubPage />) },
              { path: "eservices/poa", element: withSuspense(<PoaSubmissionPage />) },
              { path: "eservices/caf-monitor", element: withSuspense(<CafMonitorPage />) },
              { path: "eservices/mailbox", element: withSuspense(<SecureMailboxPage />) },
              {
                // Knowledge Base (05-8): firm-facing browsing under a sub-nav shell.
                // FAQ/Wiki authoring is nested behind PlatformAdminRoute (vendor-
                // global content); media/toolbox/video authoring lives in /admin/*.
                path: "kb",
                element: <KbLayout />,
                children: [
                  { index: true, element: withSuspense(<KbHomePage />) },
                  { path: "faq", element: withSuspense(<FaqBrowsePage />) },
                  { path: "faq/transcripts", element: withSuspense(<FaqTranscriptsPage />) },
                  { path: "whats-new", element: withSuspense(<KbWhatsNewPage />) },
                  { path: "library", element: withSuspense(<LibraryHomePage />) },
                  { path: "library/software", element: withSuspense(<LibraryCategoryPage />) },
                  { path: "library/resources", element: withSuspense(<LibraryCategoryPage />) },
                  { path: "library/webinars", element: withSuspense(<LibraryVideosPage />) },
                  { path: "library/resolution", element: withSuspense(<LibraryVideosPage />) },
                  { path: "toolbox", element: withSuspense(<KbToolboxPage />) },
                  { path: "videos", element: withSuspense(<KbVideosPage />) },
                  { path: "wiki/:slug", element: withSuspense(<WikiViewPage />) },
                  {
                    // Platform-admin authoring (static paths outrank wiki/:slug).
                    element: <PlatformAdminRoute />,
                    children: [
                      { path: "faq/manage", element: withSuspense(<FaqManagePage />) },
                      { path: "wiki/manage", element: withSuspense(<WikiManagePage />) },
                      { path: "library/manage", element: <Navigate to="/admin/media" replace /> },
                    ],
                  },
                ],
              },
              {
                // Client-context shell: header + tab strip shared by every
                // /client/:id/* page. The client is looked up from the :id
                // route param, so every sub-page is independently linkable.
                path: "client/:id",
                element: <ClientLayout />,
                children: [
                  { path: "overview", element: withSuspense(<ClientOverviewPage />) },
                  { path: "profile", element: withSuspense(<ClientProfilePage />) },
                  { path: "comparison", element: withSuspense(<ClientComparisonPage />) },
                  { path: "tasks", element: withSuspense(<ClientTasksPage />) },
                  { path: "cases", element: withSuspense(<ClientCasesPage />) },
                  { path: "cases/:caseId", element: withSuspense(<ClientCaseDetailPage />) },
                  {
                    path: "cases/:caseId/worksheets",
                    element: <Navigate to="personal" replace />,
                  },
                  {
                    path: "cases/:caseId/worksheets/:tab",
                    element: withSuspense(<ClientWorksheetsPage />),
                  },
                  {
                    path: "cases/:caseId/workflow/ia",
                    element: withSuspense(<ClientIAWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/workflow/ia/:step",
                    element: withSuspense(<ClientIAWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/oic",
                    element: withSuspense(<ClientOICWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/oic/:step",
                    element: withSuspense(<ClientOICWorkflowPage />),
                  },
                  {
                    path: "workflows/innocent-spouse",
                    element: withSuspense(<ClientInnocentSpouseWorkflowPage />),
                  },
                  {
                    path: "workflows/spouse-relief",
                    element: withSuspense(<ClientSpouseReliefWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/workflow/spouse-relief",
                    element: withSuspense(<ClientCaseSpouseReliefWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/workflow/excise",
                    element: withSuspense(<ClientExciseWorkflowPage />),
                  },
                  {
                    path: "cases/:caseId/workflow/excise/:step",
                    element: withSuspense(<ClientExciseWorkflowPage />),
                  },
                  { path: "tax-relief-report", element: withSuspense(<TaxReliefReportPage />) },
                  { path: "irs-forms", element: withSuspense(<ClientIrsFormsPage />) },
                  { path: "irs-forms/:formNumber", element: withSuspense(<ClientFormFillPage />) },
                  { path: "documents", element: withSuspense(<ClientDocumentsPage />) },
                  { path: "required-docs", element: withSuspense(<ClientRequiredDocsPage />) },
                  { path: "last-year-docs", element: withSuspense(<ClientLastYearDocsPage />) },
                  { path: "banking", element: withSuspense(<ClientBankingPage />) },
                  { path: "financials", element: withSuspense(<ClientFinancialsPage />) },
                  { path: "irs-notices", element: withSuspense(<ClientIrsNoticesPage />) },
                  {
                    path: "irs-notices/:noticeId",
                    element: withSuspense(<ClientNoticeDetailPage />),
                  },
                  { path: "letters", element: withSuspense(<ClientLettersPage />) },
                  {
                    path: "esignatures",
                    children: [
                      { index: true, element: withSuspense(<ClientESignaturesPage />) },
                      {
                        path: ":requestId",
                        element: withSuspense(<ClientESignatureDetailPage />),
                      },
                    ],
                  },
                  {
                    path: "efiling",
                    children: [
                      { index: true, element: withSuspense(<ClientEFilingPage />) },
                      {
                        path: ":submissionId",
                        element: withSuspense(<ClientSubmissionDetailPage />),
                      },
                    ],
                  },
                  // The list keeps the client chrome (header + tabs); the draft
                  // editor is a focused full-width route (sibling below, outside
                  // ClientLayout) to match the invoice-detail mockup.
                  { path: "invoices", element: withSuspense(<ClientInvoicesListPage />) },
                  {
                    path: "transcripts",
                    element: withSuspense(<ClientTranscriptsLayout />),
                    children: [
                      { index: true, element: withSuspense(<ClientTaxOwedPage />) },
                      { path: "payments", element: withSuspense(<ClientPaymentsPage />) },
                      { path: "income", element: withSuspense(<ClientIncomePage />) },
                      { path: "fta", element: withSuspense(<ClientFtaPage />) },
                      { path: "bankruptcy", element: withSuspense(<ClientBankruptcyPage />) },
                      { path: "requests", element: withSuspense(<TranscriptRequestsPage />) },
                      { path: "monitor", element: withSuspense(<TranscriptMonitoringPage />) },
                      // Dynamic segment last so the static tabs above resolve first.
                      {
                        path: ":taxRecordId",
                        element: withSuspense(<ClientTranscriptDetailPage />),
                      },
                    ],
                  },
                ],
              },
              // Invoice draft editor — rendered outside ClientLayout (no client
              // header/tabs) as a focused full-width editor, per the mockup.
              {
                path: "client/:id/invoices/:invoiceId",
                element: withSuspense(<ClientInvoiceEditPage />),
              },
            ],
          },
        ],
      },
    ],
  },
];

// On the dedicated portal host the handle lives at the root, so only the portal
// route groups mount (no staff shells, no root redirect — a second index route
// at `/` would collide with the portal landing). Everywhere else the full staff
// tree carries the portal under `/portal/...`.
const router = createBrowserRouter(
  isPortalHost
    ? [
        portalPublicGroup,
        portalAuthedGroup,
        { path: "*", element: withSuspense(<NotFoundPage />) },
      ]
    : staffRouteTree,
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
